import { useQuery } from "@tanstack/react-query";
import { ytgGateway } from "@modules/ytg/api/gatewayFacade";
import type { OperationStatus } from "@shared-types";
import type { OperationsStatsResponse } from "@modules/gateway/api/gateway.service";

const DEFAULT_STATUS: Partial<Record<OperationStatus, number>> = {
    pending: 0,
    in_progress: 0,
    completed: 0,
    failed: 0,
};

/** Per-client operation count for the dashboard breakdown. */
export interface DashboardStatsClient {
    id: number;
    name: string;
    short_name?: string;
    count: number;
}

/** Per-language operation count for the dashboard breakdown. */
export interface DashboardStatsLang {
    id: number;
    description: string;
    emoji_flag: string;
    count: number;
}

/** Per-user operation count for the dashboard breakdown. */
export interface DashboardStatsUser {
    id: number;
    name: string;
    email?: string;
    picture?: string | null;
    count: number;
}

/** Dashboard statistics reshaped from the raw gateway response for UI consumption. */
export interface MappedDashboardStats {
    total_operations: number;
    by_status: Partial<Record<OperationStatus, number>>;
    by_client_list: DashboardStatsClient[];
    by_lang_list: DashboardStatsLang[];
    by_user_list: DashboardStatsUser[];
    completion_rate: number;
    recent_operations: unknown[];
    total_keywords: number;
    guides_generated: number;
    guides_pending: number;
    avg_soseo_score: number;
    avg_dseo_score: number;
}

const mapStats = (raw: OperationsStatsResponse): MappedDashboardStats => ({
    total_operations: raw.total_operations || 0,
    by_status: raw.by_status || DEFAULT_STATUS,
    by_client_list: (raw.by_client || []).map((c) => ({
        id: c.client_id,
        name: c.name || `Client ${c.client_id}`,
        short_name: c.short_name ?? undefined,
        count: c.count,
    })),
    by_lang_list: (raw.by_lang || []).map((l) => ({
        id: l.lang_id,
        description: l.description || `Langue ${l.lang_id}`,
        emoji_flag: l.emoji_flag || "",
        count: l.count,
    })),
    by_user_list: (raw.by_user || []).map((u) => ({
        id: u.user_id,
        name: u.name || u.email || `Utilisateur ${u.user_id}`,
        email: u.email ?? undefined,
        picture: u.picture,
        count: u.count,
    })),
    completion_rate: raw.completion_rate || 0,
    recent_operations: [],
    total_keywords: 0,
    guides_generated: 0,
    guides_pending: 0,
    avg_soseo_score: 0,
    avg_dseo_score: 0,
});

/** Return shape of {@link useYtgDashboardStats}. */
export interface UseYtgDashboardStatsReturn {
    stats: MappedDashboardStats | null;
    loading: boolean;
    error: Error | null;
}

/**
 * Query hook returning the aggregated dashboard statistics, reshaped from the
 * raw gateway response into {@link MappedDashboardStats} via the query `select`.
 *
 * @example
 * ```tsx
 * const { stats, loading } = useYtgDashboardStats();
 * const total = stats?.total_operations ?? 0;
 * ```
 */
export const useYtgDashboardStats = (
    { mine = false }: { mine?: boolean } = {},
): UseYtgDashboardStatsReturn => {
    const { data, isLoading, error } = useQuery<OperationsStatsResponse, Error, MappedDashboardStats>({
        queryKey: ["ytg", "dashboard-stats", { mine }],
        // `mine` scopes the aggregates to the current user (gateway `?mine=true`);
        // omitted → global (used by the admin stats pages).
        queryFn: ({ signal }) =>
            ytgGateway.getOperationsStats({ signal, ...(mine ? { params: { mine: true } } : {}) }),
        select: mapStats,
    });
    return { stats: data ?? null, loading: isLoading, error };
};
